/**
 * Reações rápidas — os emojis mais usados, no estilo do Slack.
 *
 * Antes, reagir custava dois passos: abrir o picker e caçar o emoji. Como na
 * prática quase toda reação é um punhado de emojis repetidos, os mais usados
 * ficam a um clique, e o picker continua ali para o resto.
 *
 * O ranking é local (localStorage), por navegador: é preferência de uso, não
 * dado de negócio, e não vale um round-trip nem uma coluna nova. A contagem só
 * ordena — nunca esconde o picker.
 */

const STORAGE_KEY = 'tw:inbox:reaction-usage';

/** Quantos atalhos aparecem na barra de ações. */
export const QUICK_REACTION_COUNT = 3;

/**
 * Semente para quem ainda não reagiu a nada. Sem isso, a barra nasceria vazia e
 * o recurso só apareceria depois que o usuário já tivesse usado o picker.
 */
export const DEFAULT_QUICK_REACTIONS = ['👍', '❤️', '😂'];

/**
 * @returns {Record<string, number>} emoji → número de usos
 */
function readUsage() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
        // Descarta entradas corrompidas em vez de deixar NaN vazar na ordenação
        return Object.fromEntries(
            Object.entries(parsed).filter(
                ([emoji, n]) => typeof emoji === 'string' && emoji && Number.isFinite(n),
            ),
        );
    } catch {
        return {};
    }
}

/**
 * Os emojis a exibir como atalho, do mais usado para o menos.
 *
 * Completa com os padrões quando o histórico é curto, para a barra ter sempre o
 * mesmo tamanho — barra que muda de largura conforme o histórico faz os botões
 * vizinhos dançarem de lugar entre uma mensagem e outra.
 *
 * @returns {string[]} exatamente QUICK_REACTION_COUNT emojis
 */
export function getQuickReactions() {
    const usage = readUsage();

    const ordenados = Object.entries(usage)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([emoji]) => emoji);

    const resultado = [];
    for (const emoji of [...ordenados, ...DEFAULT_QUICK_REACTIONS]) {
        if (resultado.length >= QUICK_REACTION_COUNT) break;
        if (!resultado.includes(emoji)) resultado.push(emoji);
    }
    return resultado;
}

/**
 * Registra um uso. Chamado tanto pelo atalho quanto pelo picker — é o uso pelo
 * picker que faz um emoji novo subir para os atalhos.
 *
 * @param {string} emoji
 */
export function registerReactionUse(emoji) {
    if (!emoji || typeof emoji !== 'string') return;
    try {
        const usage = readUsage();
        usage[emoji] = (usage[emoji] || 0) + 1;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(usage));
    } catch {
        /* localStorage indisponível — atalhos seguem nos padrões */
    }
}
